'use client';

import {
  faTimes,
  faCheck,
  IconDefinition,
} from '@fortawesome/pro-regular-svg-icons';

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { asText, Content } from '@prismicio/client';

import {
  Box,
  Icon,
  List,
  ListOption,
  Typography,
  FontColour,
} from '@preventx/oxygen';

// Contexts
import { useClinicDetailsContext } from '@/context/ClinicDetailsProvider';

export type ServiceContainerProps = {
  slice: Content.AvailableClinicServicesSlice;
};

type AvailabilityIconConfiguration = {
  icon: IconDefinition;
  iconColour: FontColour;
};

export const ServiceContainer = ({ slice }: ServiceContainerProps) => {
  const { clinicServicesContent } = useClinicDetailsContext();

  const { id, primary, variation } = slice;
  const { clinic_services } = primary;

  const [configuration, setConfiguration] = useState<
    AvailabilityIconConfiguration | undefined
  >(undefined);

  useEffect(() => {
    setConfiguration(
      {
        default: {
          icon: faCheck,
          iconColour: 't-positive',
        } satisfies AvailabilityIconConfiguration,
        asServiceUnavailable: {
          icon: faTimes,
          iconColour: 't-negative',
        } satisfies AvailabilityIconConfiguration,
        asPotentiallyAvailable: {
          icon: faCheck,
          iconColour: 't-notice',
        } satisfies AvailabilityIconConfiguration,
      }[variation],
    );
  }, []);

  if (!clinicServicesContent || !configuration) {
    return null;
  }

  return (
    <List
      ul="stack-nav"
      className={classNames({})}
      options={clinic_services.map((clinicService, index) => {
        if (!clinicService.service) {
          throw new Error(
            'No service has been assigned to the Available Clinic Services slice',
          );
        }

        const label = {
          'Health Adviser Support':
            clinicServicesContent.health_adviser_support,
          'Asymptomatic Screening':
            clinicServicesContent.asymptomatic_screening,
          'Hepatitis B Vaccination':
            clinicServicesContent.hepatitis_b_vaccination,
          'Alcohol Support Services':
            clinicServicesContent.alcohol_support_services,
          'Chemsex Support Services':
            clinicServicesContent.chemsex_support_services,
          'Emergency Contraception Coil':
            clinicServicesContent.emergency_contraception_coil,
          'Emergency Contraception Pill':
            clinicServicesContent.emergency_contraception_pill,
          'C-Card Condom Service Collection':
            clinicServicesContent.c_card_condom_service_collection,
          'C-Card Condom Service Registration':
            clinicServicesContent.c_card_condomservice_registration,

          'PEP HIV': clinicServicesContent.pep_hiv,
          'PrEP HIV': clinicServicesContent.prep_hiv,
          'LARC Coil': clinicServicesContent.larc_coil,
          'LARC Implant': clinicServicesContent.larc_implant,
          'HIV Management': clinicServicesContent.hiv_management,
          'PEP Hepatitis B': clinicServicesContent.pep_hepatitis_b,
          'Symptomatic MSW': clinicServicesContent.symptomatic_msw,
          'Symptomatic Female': clinicServicesContent.symptomatic_female,
          'Treatment Syphilis': clinicServicesContent.treatment_syphilis,
          'Under 16 Support': clinicServicesContent.under_sixteen_support,
          '16-17 Support': clinicServicesContent.sixteen_seventeen_support,
          'Treatment Chlamydia': clinicServicesContent.treatment_chlamydia,
          'Treatment Gonorrhoea': clinicServicesContent.treatment_gonorrhoea,
          'Confirmatory Testing': clinicServicesContent.confirmatory_testing,
          'Symptomatic MSM Trans': clinicServicesContent.symptomatic_msm_trans,
          'General Contraception': clinicServicesContent.general_contraception,
          'Drug Support Services': clinicServicesContent.drug_support_services,
        }[clinicService.service];

        const { icon, iconColour } = configuration;

        return {
          key: `available-clinic-services-${id}-${index + 1}`,
          content: (
            <Box addRepel component="div">
              <Box addCluster component="div" className="media-object">
                <Icon icon={icon} colour={iconColour} />
                <Box component="div">
                  <Typography component="span">{asText(label)}</Typography>
                  <Box component="div">
                    <Typography component="small">
                      {asText(clinicService.help_message)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ),
        } satisfies ListOption;
      })}
    />
  );
};
